import React, { useEffect, useState } from "react";
import api from "../api";
import { hasPermission } from "../utils/auth";

export default function ProposalUploadPage({ user }) {
  const [title, setTitle] = useState("");
  const [contractTypeId, setContractTypeId] = useState("");
  const [contractTypes, setContractTypes] = useState([]);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const canUpload = hasPermission(user, "proposals:create");
  const canReadTypes = hasPermission(user, "contract-types:read");

  const fetchTypes = async () => {
    if (!canReadTypes) return;
    try {
      const res = await api.get("/contract-types");
      setContractTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Type fetch error:", err);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, [canReadTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!title.trim()) {
      setMessage("Title is required.");
      return;
    }
    if (!file) {
      setMessage("PDF file is required.");
      return;
    }
    try {
      setLoading(true);
      const form = new FormData();
      form.append("title", title.trim());
      if (contractTypeId) form.append("contract_type_id", contractTypeId);
      form.append("file", file);
      const res = await api.post("/proposals", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage(res.data?.message || "Proposal uploaded.");
      setTitle("");
      setContractTypeId("");
      setFile(null);
    } catch (err) {
      console.error("Proposal upload error:", err);
      setMessage(err.response?.data?.error || "Failed to upload proposal.");
    } finally {
      setLoading(false);
    }
  };

  if (!canUpload) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
          You do not have permission to upload proposals.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-navy">Upload Proposal</h1>
        <p className="text-sm text-slate-600 mt-1">
          Add a new proposal for review. Only title and type are required.
        </p>
      </div>

      {message && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Proposal title
          </label>
          <input
            type="text"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Liefervertrag 2025"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Contract type
          </label>
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={contractTypeId}
            onChange={(e) => setContractTypeId(e.target.value)}
          >
            <option value="">Select type</option>
            {contractTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            PDF file
          </label>
          <input
            type="file"
            accept="application/pdf"
            className="w-full text-sm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-60"
          >
            {loading ? "Uploading..." : "Upload Proposal"}
          </button>
        </div>
      </form>
    </div>
  );
}
